import React, {useState} from 'react';
import { SingleDatePicker } from 'react-dates';
import TimePicker from 'rc-time-picker';
import {produce} from 'immer';
import moment from "moment";
import {Form} from "react-bootstrap";
import ValidationError from "~/Components/Forms/ValidationError";
import TextTranslate, { TextTranslateString } from '../../../Components/TextTranslate/TextTranslate';
import { connect } from 'react-redux';

const FlightRow = ({onChange, values, errors, type, ...props}) => {

    const dateFocusInitialState = {from: false, to: false};
    const [dateFocused, setDateFocused] = useState(dateFocusInitialState);

    const defaultFields = {
        flightNumber: '',
        airline: '',
        from: {
            location: '',
            terminal: '',
            date: null,
            time: null,
        },
        to: {
            location: '',
            terminal: '',
            date: null,
            time: null,
        },
        airportTransferToCity: false,
        stopOvers: '',
        comments: ''
    };

    const form = {...defaultFields, ...values};

    const flightRowChanged = (value, key, field) => {
        onChange(produce(form, (draft) => {
            draft[key][field] = value;
        }));
    };

    const flightTimeChanged = (value, key) => {
        onChange(produce(form, (draft) => {
            if (value.isValid()) {
                value.seconds(0);
                draft[key].time = moment.utc(value);

            } else {
                draft[key].time = null;
            }
        }));
    };

    const flightChanged = (e, field) => {
        let value;
        
        if(field === 'airportTransferToCity') {
            value = e.target.checked;
        } else {
            value = e.target.value;
        }

        onChange(produce(form, (draft) => {
            draft[field] = value;
        }));
    };

    const dateFocusChanged = (f, key) => {
        setDateFocused({...dateFocusInitialState, [key] : f.focused});
    }

    let travelPoints = {
        from: 'Departure',
        to: 'Arrival',
    }

    let Error = (props) => {
        let key = 'flight.' + type + '.' + props.name;

        return (
            <ValidationError errors={errors} name={key} />
        )
    };

    return (
        <>
            <div className="flex-gap">
                <div className="flex-1">
                    <label><TextTranslate name="flight-flight-nr" fallback="Flight #" textOnly/></label>
                    <input type="text"
                           value={form.flightNumber}
                           onChange={(e) => flightChanged(e, 'flightNumber')}
                           className="form-control"/>

                    <Error name="flightNumber" />
                </div>
                <div className="flex-2">
                    <label><TextTranslate name="flight-airline" fallback="Airline" textOnly/></label>
                    <input type="text"
                           value={form.airline}
                           onChange={(e) => flightChanged(e, 'airline')}
                           className="form-control"/>

                    <Error name="airline" />
                </div>
            </div>
            <hr/>
            {Object.keys(travelPoints).map((key) => {
                return (
                    <div key={key} className="flex-gap mb-3">
                        <div className="flex-gap-column justify-content-between smart-resize">
                            <label className="text-capitalize"><TextTranslate name={`flight-${key}`} fallback={`${key}`} textOnly/></label>
                            <input type="text"
                                   placeholder={`${travelPoints[key]} Location`}
                                   value={form[key].location}
                                   onChange={(e) => flightRowChanged(e.target.value, key, 'location')}
                                   className="form-control"/>

                            <Error name={key + '.location'} />
                        </div>
                        <div className="flex-gap-column justify-content-between smart-resize">
                            <label><TextTranslate name="flight-terminal" fallback="Terminal" textOnly/></label>
                            <input type="text"
                                   placeholder="Airport Terminal"
                                   value={form[key].terminal}
                                   onChange={(e) => flightRowChanged(e.target.value, key, 'terminal')}
                                   className="form-control"/>

                            <Error name={key + '.terminal'} />
                        </div>
                        <div className="flex-gap-column flex-1">
                            <label><TextTranslate name={`flight-${travelPoints[key].toLowerCase()}-date`} fallback="Departure Date" textOnly/></label>
                            <SingleDatePicker
                                readOnly={true}
                                isRTL={props.currentLanguage?.rtl}
                                placeholder={TextTranslateString(`flight-${travelPoints[key].toLowerCase()}-date`, "Date", props.currentLanguage, props.translations)}
                                id={`${type}_${key}_date`}
                                small={true}
                                focused={dateFocused[key]}
                                date={form[key].date ? moment(form[key].date).locale(props.currentLanguage.code) : undefined}
                                onDateChange={(date) => flightRowChanged(date, key, 'date')}
                                onFocusChange={(f) => dateFocusChanged(f, key)}
                                openDirection={'up'}
                                keepOpenOnDateSelect={false}
                                hideKeyboardShortcutsPanel={true}
                                numberOfMonths={1}
                                monthFormat='MMM YYYY'
                                displayFormat='ddd DD MMM'
                                renderMonthElement={({ month }) => moment(month).locale(props.currentLanguage.code).format('MMMM YYYY')}
                                renderDayContents={(day) => moment(day).locale(props.currentLanguage.code).format('D')}
                            />

                            <Error name={key + '.date'} />
                        </div>
                        <div className="flex-gap-column flex-1">
                            <label><TextTranslate name={`flight-${travelPoints[key].toLowerCase()}-time`} fallback="Departure Time" textOnly/></label>
                            <TimePicker
                                placeholder="00:00"
                                popupClassName="timepicker-neom"
                                onChange={(time) => flightTimeChanged(time, key)}
                                defaultValue={form[key].time}
                                showSecond={false} />

                            <Error name={key + '.time'} />
                        </div>
                    </div>
                )
            })}

            <div className="row mb-3">
                <div className="col-12">
                    <Form.Check
                        onChange={(e) => flightChanged(e, 'airportTransferToCity')}
                        inline
                        checked={form.airportTransferToCity}
                        type="checkbox"
                        label={<TextTranslate name="flight-need-transfer" fallback="Please confirm if you require airport transfers in host city?" textOnly/>}
                    />
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-12">
                    <label><TextTranslate name="flight-stopovers" fallback="Stopovers" textOnly/></label>
                    <small className="d-block mb-2"><TextTranslate name="flight-stopovers-note" fallback="Please list any stopovers you may have." textOnly/></small>
                    <input type="text"
                           value={form.stopOvers}
                           onChange={(e) => flightChanged(e, 'stopOvers')}
                           className="form-control"/>

                    <Error name="stopOvers" />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-12">
                    <label><TextTranslate name="word-comments" fallback="Comments"/></label>
                    <small className="d-block mb-2"><TextTranslate name="travel-comments-note" fallback="Please specify any additional details or comments." textOnly/></small>
                    <textarea
                        onChange={e => {flightChanged(e, 'comments')}}
                        className="form-control" rows="3" defaultValue={form.comments} />

                    <Error name="comments" />
                </div>
            </div>

        </>
    )
}

const mapStateToProps = (state) => ({
    currentLanguage: state.translations?.currentLanguage,
    translations: state.translations?.translations,
});

export default connect(mapStateToProps)(FlightRow);
